import * as dynamicElementKeys from '@epicuro-next/constants/dynamic-elements';
import { RemoveOptional, StrictOmit } from '@epicuro-next/utilities';

import { Operation } from './operation.interface';

export interface IId {
  id?: string;
}

export interface IDynamicPage {
  items: IDynamicItem[];
  styles: Partial<IDynamicItemStyles>;
}

export interface IDynamicBindableValueOptions {
  transform?: string;
  startWith?: { value: unknown };
  wrapWithLoadingState?: boolean;
  editable?: boolean;
  onError?: IDynamicEventHandler | IDynamicEventHandler[];
  filter?: string;
  pairWise?: boolean;
}

const dummyOptions: Record<
  keyof Required<IDynamicBindableValueOptions>,
  undefined
> = {
  editable: undefined,
  onError: undefined,
  startWith: undefined,
  transform: undefined,
  wrapWithLoadingState: undefined,
  filter: undefined,
  pairWise: undefined,
};

const optionKeys = Object.keys(
  dummyOptions,
) as (keyof IDynamicBindableValueOptions)[];

export function isIDynamicBindableValueOptions(
  value: unknown,
): value is IDynamicBindableValueOptions {
  if (value === undefined || value === null || typeof value !== 'object') {
    return false;
  }

  const keysInValue = Object.keys(value);
  const intersectionWithOptionKeys = keysInValue.filter((key) =>
    optionKeys.includes(key as keyof IDynamicBindableValueOptions),
  );
  return intersectionWithOptionKeys.length > 0;
}

export interface IDynamicBindableValue extends IDynamicBindableValueOptions {
  provider: string;
  property: IDynamicBindableValue | IDynamicBindableValueCombination | unknown;
}

export function isIDynamicBindableValue(
  itemValue: unknown,
): itemValue is IDynamicBindableValue {
  const providerKey: keyof IDynamicBindableValue = 'provider';
  const propertyKey: keyof IDynamicBindableValue = 'property';

  return (
    typeof itemValue === 'object' &&
    itemValue !== null &&
    providerKey in itemValue &&
    propertyKey in itemValue
  );
}

export interface IDynamicBindableValueCombination
  extends IDynamicBindableValueOptions {
  values: (IDynamicBindableValue | IDynamicBindableValueCombination)[];
}

export function isIDynamicBindableValueCombination(
  value: unknown,
): value is IDynamicBindableValueCombination {
  const valuesKey: keyof IDynamicBindableValueCombination = 'values';

  return (
    value !== undefined &&
    value !== null &&
    typeof value === 'object' &&
    valuesKey in value &&
    Array.isArray(value[valuesKey])
  );
}

export type IDynamicItemValue =
  | IDynamicBindableValue
  | IDynamicBindableValueCombination
  | string
  | string[]
  | boolean
  | number
  | number[]
  | Record<string, unknown>
  | Record<string, unknown>[]
  | object
  | null;

export interface IDynamicItemStylesBindableValue {
  check: Operation;
  value: string;
}

export interface IDynamicItemStyles {
  classes?: (string | IDynamicItemStylesBindableValue)[];
  css?: string | IDynamicBindableValue | IDynamicBindableValueCombination;
  style?: Record<
    string,
    string | IDynamicBindableValue | IDynamicBindableValueCombination
  >;
  dynamicClasses?: IDynamicBindableValue | IDynamicBindableValueCombination;
  dynamicVariables?: IDynamicBindableValue | IDynamicBindableValueCombination;
  variables?: Record<string, string | IDynamicItemStylesBindableValue>;
}

export type IDynamicInput = IDynamicItem | IDynamicItem[] | IDynamicItemValue;
export type IDynamicInputs = Record<string, IDynamicInput>;
export type IDynamicOutputs = {
  [key: string]: IDynamicEventHandler | IDynamicEventHandler[];
};

/**
 * Payloads for events can be resolved at different moments.
 * Useful in cases where events alter states which could affect the payload in a way that was not intended.
 * For instance when some dispatched NgRx action navigates away, destroys all ANgular components,
 * therefore there is no parent component anymore, and hence provider: 'parent' throws an error
 * that no parent was found. In this case the AtEventAndBeforeAnyEventHandlers could be used.
 */
export enum EventHandlerPayloadResolveMoment {
  /**
   * If not specified this is the default. The moment the event is being handled the payload is resolved.
   */
  AtEvent = 'AtEvent',
  /**
   * Specify this if the payload would be affected by preceeding events.
   * The payload is still resolved upon the event, but before any eventhandlers for this event
   * are executed. A practical example: the onClick event is handled by 3 eventhandlers.
   * To resolve the payload before the 3 eventhandlers are effectively executed, specify this value.
   */
  AtEventAndBeforeAnyEventHandlersAreExecuted = 'AtEventAndBeforeAnyEventHandlersAreExecuted',
  /**
   * Specify this if the payload would be resolved before adding event listener.
   * A practical example: the onClick event has payload from Link component to resolve.
   * Resolving the payload after onClick event leads to the error as component can be destroyed due to link navigation
   * To resolve payload and then add event listener with static value use this value
   */
  BeforeAddEventListener = 'BeforeAddEventListener',
}

export type EventHandlerType = 'ngrx' | 'custom-event';

export interface IDynamicEventHandler {
  type: EventHandlerType;
  action: string;
  payload$?: unknown;
  payloadFetchMoment?: EventHandlerPayloadResolveMoment;
}

export type DynamicElementKey =
  typeof dynamicElementKeys[keyof typeof dynamicElementKeys];

export interface IDynamicItem extends IId {
  contentTypeId: DynamicElementKey | string;
  styles?: Partial<IDynamicItemStyles>;
  if?: Operation;
  inputs?: IDynamicInputs;
  children?: IDynamicItem[];
  data?: IDynamicInputs;
  eventHandlers?: {
    onClick?: IDynamicEventHandler | IDynamicEventHandler[];
    onVisibleOnce?: IDynamicEventHandler | IDynamicEventHandler[];
  };
  outputs?: { [key: string]: IDynamicEventHandler | IDynamicEventHandler[] };
  attributes?: { [key: string]: string | IDynamicItemValue };
  events?:
    | (IDynamicBindableValue | IDynamicBindableValueCombination)
    | (IDynamicBindableValue | IDynamicBindableValueCombination)[];
}

export function isIDynamicItem(value: unknown): value is IDynamicItem {
  if (value === undefined || value === null || typeof value !== 'object') {
    return false;
  }

  const dummyDynamicItem: Record<
    keyof RemoveOptional<IDynamicItem>,
    undefined
  > = {
    contentTypeId: undefined,
  };

  const keysToCheck = Object.keys(dummyDynamicItem);
  return keysToCheck.every((key) => key in value);
}

export interface IDynamicItemMergeOptions {
  source: string;
  key: string;
}

export function isIDynamicItemMergeOptions(
  value: unknown,
): value is IDynamicItemMergeOptions {
  if (value === undefined || value === null || typeof value !== 'object') {
    return false;
  }

  const dummyDynamicItemMergeOptions: Record<
    keyof RemoveOptional<IDynamicItemMergeOptions>,
    undefined
  > = {
    source: undefined,
    key: undefined,
  };

  const keysToCheck = Object.keys(dummyDynamicItemMergeOptions);
  return keysToCheck.every((key) => key in value);
}

export type DynamicItemsConfigEntry =
  | PartialIDynamicItem
  | PartialIDynamicItem[]
  | IDynamicItemMergeOptions;

export type WithMergeMethod = { mergeMethod?: string };
export type PartialIDynamicItem = StrictOmit<
  Partial<IDynamicItem>,
  'children'
> & {
  children?: PartialIDynamicItem[];
};

export type PartialIDynamicItemExt = PartialIDynamicItem & WithMergeMethod;
export type PartialConfig = PartialIDynamicItemExt[] | PartialIDynamicItemExt;
